<template>
  <div class="chat-message" :class="[message.role, { 'error': message.error }]">
    <div class="message-avatar">
      <span v-if="message.role === 'user'">👤</span>
      <span v-else>🤖</span>
    </div>
    
    <div class="message-content">
      <div class="message-text" v-html="formattedContent"></div>
      
      <!-- Confidence Score (for AI messages) -->
      <div v-if="message.role === 'assistant' && message.confidence_score" class="message-meta">
        <span class="confidence-badge" :class="confidenceClass">
          Confidence: {{ Math.round(message.confidence_score * 100) }}%
        </span>
        <span v-if="message.intent" class="intent-badge">
          {{ formatIntent(message.intent) }}
        </span>
      </div>

      <!-- Action Buttons -->
      <div v-if="message.actions && message.actions.length" class="action-buttons">
        <button
          v-for="(action, index) in message.actions"
          :key="index"
          @click="handleAction(action)"
          class="action-btn"
          :class="getActionClass(action)"
        >
          <c-icon v-if="getActionIcon(action)" :name="getActionIcon(action)" />
          <i v-else-if="action.icon" :class="action.icon"></i>
          {{ action.label }}
        </button>
      </div>

      <!-- Follow-up Questions -->
      <div v-if="message.followUpQuestions && message.followUpQuestions.length" class="follow-up-questions">
        <div class="follow-up-label">Suggestions:</div>
        <button
          v-for="(question, index) in message.followUpQuestions"
          :key="index"
          @click="$emit('send-followup', question)"
          class="follow-up-btn"
        >
          {{ question }}
        </button>
      </div>

      <!-- Feedback Buttons (for AI messages) -->
      <div v-if="message.role === 'assistant' && !message.error" class="message-actions">
        <button
          @click="$emit('mark-helpful', message.id)"
          class="feedback-btn"
          :class="{ 'active': message.helpful === true }"
          title="Helpful"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.864.046C7.908-.193 7.02.53 6.956 1.466c-.072 1.051-.23 2.016-.428 2.59-.125.36-.479 1.013-1.04 1.639-.557.623-1.282 1.178-2.131 1.41C2.685 7.288 2 7.87 2 8.72v4.001c0 .845.682 1.464 1.448 1.545 1.07.114 1.564.415 2.068.723l.048.03c.272.165.578.348.97.484.397.136.861.217 1.466.217h3.5c.937 0 1.599-.477 1.934-1.064a1.86 1.86 0 0 0 .254-.912c0-.152-.023-.312-.077-.464.201-.263.38-.578.488-.901.11-.33.172-.762.004-1.149.069-.13.12-.269.159-.403.077-.27.113-.568.113-.857 0-.288-.036-.585-.113-.856a2.144 2.144 0 0 0-.138-.362 1.9 1.9 0 0 0 .234-1.734c-.206-.592-.682-1.1-1.2-1.272-.847-.282-1.803-.276-2.516-.211a9.84 9.84 0 0 0-.443.05 9.365 9.365 0 0 0-.062-4.509A1.38 1.38 0 0 0 9.125.111L8.864.046zM11.5 14.721H8c-.51 0-.863-.069-1.14-.164-.281-.097-.506-.228-.776-.393l-.04-.024c-.555-.339-1.198-.731-2.49-.868-.333-.036-.554-.29-.554-.55V8.72c0-.254.226-.543.62-.65 1.095-.3 1.977-.996 2.614-1.708.635-.71 1.064-1.475 1.238-1.978.243-.7.407-1.768.482-2.85.025-.362.36-.594.667-.518l.262.066c.16.04.258.143.288.255a8.34 8.34 0 0 1-.145 4.725.5.5 0 0 0 .595.644l.003-.001.014-.003.058-.014a8.908 8.908 0 0 1 1.036-.157c.663-.06 1.457-.054 2.11.164.175.058.45.3.57.65.107.308.087.67-.266 1.022l-.353.353.353.354c.043.043.105.141.154.315.048.167.075.37.075.581 0 .212-.027.414-.075.582-.05.174-.111.272-.154.315l-.353.353.353.354c.047.047.109.177.005.488a2.224 2.224 0 0 1-.505.805l-.353.353.353.354c.006.005.041.05.041.17a.866.866 0 0 1-.121.416c-.165.288-.503.56-1.066.56z"/>
          </svg>
        </button>
        <button
          @click="$emit('mark-not-helpful', message.id)"
          class="feedback-btn"
          :class="{ 'active': message.helpful === false }"
          title="Not helpful"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.864 15.674c-.956.24-1.843-.484-1.908-1.42-.072-1.05-.23-2.015-.428-2.59-.125-.36-.479-1.012-1.04-1.638-.557-.624-1.282-1.179-2.131-1.41C2.685 8.432 2 7.85 2 7V3c0-.845.682-1.464 1.448-1.546 1.07-.113 1.564-.415 2.068-.723l.048-.029c.272-.166.578-.349.97-.484C6.931.08 7.395 0 8 0h3.5c.937 0 1.599.478 1.934 1.064.164.287.254.607.254.913 0 .152-.023.312-.077.464.201.262.38.577.488.9.11.33.172.762.004 1.15.069.13.12.268.159.403.077.27.113.567.113.856 0 .289-.036.586-.113.856-.035.12-.08.244-.138.363.394.571.418 1.2.234 1.733-.206.592-.682 1.1-1.2 1.272-.847.283-1.803.276-2.516.211a9.877 9.877 0 0 1-.443-.05 9.364 9.364 0 0 1-.062 4.51c-.138.508-.55.848-1.012.964l-.261.065zM11.5 1H8c-.51 0-.863.068-1.14.163-.281.097-.506.229-.776.393l-.04.025c-.555.338-1.198.73-2.49.868-.333.035-.554.29-.554.55V7c0 .255.226.543.62.65 1.095.3 1.977.997 2.614 1.709.635.71 1.064 1.475 1.238 1.977.243.7.407 1.768.482 2.85.025.362.36.595.667.518l.262-.065c.16-.04.258-.144.288-.255a8.34 8.34 0 0 0-.145-4.726.5.5 0 0 1 .595-.643h.003l.014.004.058.013a8.912 8.912 0 0 0 1.036.157c.663.06 1.457.054 2.11-.163.175-.059.45-.301.57-.651.107-.308.087-.67-.266-1.021L12.793 7l.353-.354c.043-.042.105-.14.154-.315.048-.167.075-.37.075-.581 0-.211-.027-.414-.075-.581-.05-.174-.111-.273-.154-.315l-.353-.354.353-.354c.047-.047.109-.176.005-.488a2.224 2.224 0 0 0-.505-.804l-.353-.354.353-.354c.006-.005.041-.05.041-.17a.866.866 0 0 0-.121-.415C12.4 1.272 12.063 1 11.5 1z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatMessage',

  props: {
    message: {
      type: Object,
      required: true,
    },
  },

  computed: {
    formattedContent() {
      let content = this.message.content || '';
      
      // Convert markdown-style formatting
      content = content
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Code blocks
        .replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>')
        // Inline code
        .replace(/`(.*?)`/g, '<code>$1</code>')
        // Line breaks
        .replace(/\n/g, '<br>');

      return content;
    },

    confidenceClass() {
      const score = this.message.confidence_score;
      if (score >= 0.8) return 'high';
      if (score >= 0.5) return 'medium';
      return 'low';
    },
  },

  methods: {
    formatIntent(intent) {
      return intent
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },

    handleAction(action) {
      console.log('Handling action:', action);
      if (action.type === 'link' || action.type === 'navigate') {
        if (action.url) {
          this.$router.push(action.url);
        }
      } else if (action.type === 'guide' || action.type === 'workflow') {
        // Open WorkflowGuide component
        this.openWorkflowGuide(action);
        console.log('Opening WorkflowGuide for action:', action);
        
      }
    },

    /**
     * Open WorkflowGuide from AI chat action
     */
    openWorkflowGuide(action) {
      const payload = {
        context: {
          source: 'ai_chat',
          message_id: this.message.id,
          conversation_uuid: this.message.conversationUuid,
          page: this.$route?.name,
          page_url: this.$route?.path,
          ...action.context,
        },
      };

      // If specific workflow ID is provided
      if (action.workflow_id || action.workflowId) {
        payload.workflowId = action.workflow_id || action.workflowId;
      } 
      // Otherwise use intent
      else if (action.intent) {
        payload.intent = action.intent;
      }
      // Fallback to extracting intent from action label or message intent
      else {
        payload.intent = action.label?.toLowerCase().replace(/\s+/g, '_') || this.message.intent || 'get_help';
      }

      // Emit event to open WorkflowGuide
      this.$root.$emit('open-workflow-guide', payload);
      
      // Also emit to parent for tracking
      this.$emit('start-guide', action);
    },

    /**
     * Get appropriate icon for action type
     */
    getActionIcon(action) {
      // If action has custom icon, use it
      if (action.icon && action.icon.startsWith('cil')) {
        return action.icon;
      }

      // Default icons based on action type
      const iconMap = {
        'guide': 'cilTask',
        'workflow': 'cilListNumbered',
        'navigate': 'cilArrowRight',
        'link': 'cilExternalLink',
        'tutorial': 'cilBook',
        'help': 'cilLifeRing',
      };

      return iconMap[action.type] || null;
    },

    /**
     * Get CSS class for action button based on type
     */
    getActionClass(action) {
      const classMap = {
        'guide': 'action-guide',
        'workflow': 'action-workflow',
        'navigate': 'action-navigate',
        'link': 'action-link',
        'tutorial': 'action-tutorial',
        'help': 'action-help',
      };

      return classMap[action.type] || '';
    },
  },
};
</script>

<style scoped lang="scss">
.chat-message {
  display: flex;
  gap: 12px;
  animation: fadeIn 0.3s ease;

  &.user {
    flex-direction: row-reverse;

    .message-content {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #ffffff;
      border-radius: 16px 16px 4px 16px;
    }
  }

  &.assistant {
    .message-content {
      background: #f5f5f5;
      color: #333;
      border-radius: 16px 16px 16px 4px;
    }

    .dark-mode & .message-content {
      background: #2a2a2a;
      color: #e0e0e0;
    }
  }

  &.error {
    .message-content {
      background: #ffebee;
      color: #c62828;
      border: 1px solid #ef5350;
    }

    .dark-mode & .message-content {
      background: #3a1a1a;
      color: #ff8a80;
      border-color: #d32f2f;
    }
  }
}

.message-avatar {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background: #e0e0e0;
  border-radius: 50%;
  font-size: 20px;
  line-height: 36px;

  .dark-mode & {
    background: #3a3a3a;
  }
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  word-wrap: break-word;
}

.message-text {
  font-size: 14px;
  line-height: 1.5;

  ::v-deep {
    strong {
      font-weight: 600;
    }

    em {
      font-style: italic;
    }

    code {
      padding: 2px 6px;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 13px;
    }

    pre {
      margin: 8px 0;
      padding: 12px;
      background: rgba(0, 0, 0, 0.05);
      border-radius: 8px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
      }
    }
  }

  .user & {
    ::v-deep code,
    ::v-deep pre {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.message-meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.confidence-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  
  &.high {
    background: #c8e6c9;
    color: #2e7d32;
  }

  &.medium {
    background: #fff9c4;
    color: #f57f17;
  }

  &.low {
    background: #ffcdd2;
    color: #c62828;
  }

  .dark-mode & {
    &.high {
      background: #1b5e20;
      color: #a5d6a7;
    }

    &.medium {
      background: #f57f17;
      color: #fff59d;
    }

    &.low {
      background: #b71c1c;
      color: #ef9a9a;
    }
  }
}

.intent-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: #e3f2fd;
  color: #1976d2;

  .dark-mode & {
    background: #0d47a1;
    color: #90caf9;
  }
}

.action-buttons {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  // Variant styles for different action types
  &.action-guide,
  &.action-workflow {
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
    
    &:hover {
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    }
  }

  &.action-help,
  &.action-tutorial {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    
    &:hover {
      box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
  }

  &.action-link {
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
    
    &:hover {
      box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
    }
  }
}

.follow-up-questions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  .dark-mode & {
    border-top-color: rgba(255, 255, 255, 0.1);
  }
}

.follow-up-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;

  .dark-mode & {
    color: #b0b0b0;
  }
}

.follow-up-btn {
  display: block;
  width: 100%;
  padding: 8px 12px;
  margin-bottom: 6px;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  .dark-mode & {
    background: rgba(102, 126, 234, 0.15);
    color: #8b9aed;
    border-color: rgba(102, 126, 234, 0.4);

    &:hover {
      background: rgba(102, 126, 234, 0.25);
      border-color: rgba(102, 126, 234, 0.6);
    }
  }
}

.message-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.feedback-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 8px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
  }

  &.active {
    background: #667eea;
    color: #ffffff;
  }

  .dark-mode & {
    background: rgba(255, 255, 255, 0.1);
    color: #b0b0b0;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #e0e0e0;
    }

    &.active {
      background: #8b9aed;
      color: #ffffff;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
